<template>
  <span
    v-if="node.type == 'constant'"
    ref="nodeElement"
    :contenteditable="editable"
    class="expression-input-node expression-input-node--constant"
    :data-id="node.id"
    :data-placeholder="placeholder"
    :class="{ '-empty': node.value == '' }"
    v-bind="$attrs"
    @pointerdown="onConstantMouseDown"
    @pointermove="onConstantMouseMove"
  >
    {{ node.value.replaceAll(".", ",") }}
  </span>

  <span
    v-else-if="node.type == 'operator'"
    ref="nodeElement"
    class="expression-input-node expression-input-node--operator"
    :data-id="node.id"
    :style="{
      '--node-level': level,
      '--group-color':
        node.op == '+' ? 'limegreen' : node.op == '-' ? 'red' : 'pink'
    }"
    :class="{
      '-no-break': node.op == ''
    }"
    v-bind="$attrs"
    @pointerdown="onMouseDown"
  >
    <expression-input-node
      :ref="(el) => (childNodeElements[0] = el as ChildNodeElement)"
      :node="node.args[0]"
      :level="level + 1"
      :nesting-level="nestingLevel"
      :editable="editable"
      @focus-next-node="childNodeElements[1]?.focusLeft()"
      @focus-previous-node="emit('focus-previous-node')"
      @focus-start="emit('focus-start', $event)"
      @focus-end="emit('focus-end', $event)"
    />
    <span
      class="expression-input-node__operator"
      :class="{ '-implicit': node.op == '' }"
      @pointerdown="onOperatorMousedown"
    >
      <template v-if="node.op == '==' && operatorIsAtStart">=</template>
      <template v-else>{{ node.op }}</template>
    </span>
    <expression-input-node
      :ref="(el) => (childNodeElements[1] = el as ChildNodeElement)"
      :node="node.args[1]"
      :level="level + 1"
      :nesting-level="nestingLevel"
      :editable="editable"
      @focus-previous-node="childNodeElements[0]?.focusRight()"
      @focus-next-node="emit('focus-next-node')"
      @focus-start="emit('focus-start', $event)"
      @focus-end="emit('focus-end', $event)"
    />
  </span>

  <span v-else class="expression-input-node__line" :class="{ '-multiline': isMultiline }">
    <span
      ref="anchorBefore"
      :contenteditable="editable"
      class="expression-input-node__cursorholder -before"
      :data-connected-id="node.id"
      @pointerdown="onAnchorBeforeMouseDown"
      @pointermove="onAnchorBeforeMouseMove"
    ></span>

    <span
      v-if="node.type == 'function'"
      ref="nodeElement"
      class="expression-input-node expression-input-node--function expression-input-node--anchored"
      :class="{ '-multiline': isMultiline }"
      :style="{
        '--level-color': LEVEL_COLORS[nestingLevel % LEVEL_COLORS.length]
      }"
      :data-id="node.id"
      :data-function-name="node.name"
      v-bind="$attrs"
      @pointerdown="onMouseDown"
    >
      <span
        class="expression-input-node__functionopen"
        ref="functionOpenElement"
        :style="{ color: 'var(--level-color)' }"
        @pointerenter="onMouseEnter"
        @pointermove="onMouseMove"
        @pointerleave="onMouseLeave"
        @pointerdown="onBeginningMousedown"
      >
        <span class="expression-input-node__functionname">
          <span>#</span>{{ name }} </span
        >&#40;
      </span>
      <span class="expression-input-node__functionbody">
        <template v-for="(argNode, argIndex) of node.args" :key="argNode.id">
          <expression-input-node
            :ref="
              (el) => (childNodeElements[argIndex] = el as ChildNodeElement)
            "
            :node="argNode"
            :level="level + 1"
            :nesting-level="nestingLevel + 1"
            :editable="editable"
            @focus-start="emit('focus-start', $event)"
            @focus-end="emit('focus-end', $event)"
          />
          <span
            v-if="argIndex < node.args.length - 1"
            class="expression-input-node__functionseparator"
            @pointerdown="onFunctionSeparatorMousedown($event, argIndex)"
          >
            {{ connectors[argIndex] }}
          </span>
        </template>
      </span>
      <span
        ref="functionCloseElement"
        class="expression-input-node__functionclose"
        :style="{ color: 'var(--level-color)' }"
        @pointerdown="onEndMousedown"
      >
        &#41;
      </span>

      <floating-element
        v-if="tooltip.open"
        :x="tooltip.x"
        :y="tooltip.y"
        class="expression-input-node__tooltip"
      >
        <header class="expression-input-node__tooltip-header">
          <span class="expression-input-node__tooltip-type">
            {{ name }}
          </span>
        </header>
        <section class="expression-input-node__tooltip-body">
          <span class="pv-2 expression-input-node__tooltip-value">
            {{ formatNumber(evaluatedNode?.value) }}
          </span>
        </section>
      </floating-element>
    </span>

    <span
      v-else-if="node.type == 'item'"
      ref="nodeElement"
      class="expression-input-node expression-input-node--item expression-input-node--anchored"
      :class="{ '-invalid': node.valid == false }"
      :data-id="node.id"
      :data-item-id="nodeItem?.id"
      :data-item-type="nodeItem?.type"
      :data-item-uid="nodeItem?.uid"
      :title="node.valid ? '' : $t('errors.invalidElement')"
      v-bind="$attrs"
      @pointerenter="onMouseEnter"
      @pointermove="onMouseMove"
      @pointerleave="onMouseLeave"
      @pointerdown="onItemMouseDown"
    >
      <span class="expression-input-node__prefix">@</span
      ><span
        class="expression-input-node__item"
        :style="{
          backgroundColor: toValue(node.fill) || 'transparent',
          color: toValue(node.textColor) || 'var(--color-text)'
        }"
        >{{ node.title }}</span
      >

      <floating-element
        v-if="tooltip.open"
        :x="tooltip.x"
        :y="tooltip.y"
        class="expression-input-node__tooltip"
      >
        <header class="expression-input-node__tooltip-header">
          <span class="expression-input-node__tooltip-type">{{
            $t(`objectNames.${nodeItem?.type}`)
          }}</span>
          <h3 class="expression-input-node__tooltip-title">{{ node.title }}</h3>
        </header>
        <section class="expression-input-node__tooltip-body">
          <span class="pv-2 expression-input-node__tooltip-value">
            {{ formatNumber(itemExpressionValue?.value) }}
            <span class="expression-input-node__tooltip-unit">{{
              itemExpressionValue?.unit
            }}</span>
          </span>

          <div>
            {{ formatNumber(itemExpressionValue?.minValue) }} –
            {{ formatNumber(itemExpressionValue?.maxValue) }}
          </div>
        </section>
      </floating-element>
    </span>

    <span
      v-else-if="node.type == 'variable'"
      ref="nodeElement"
      class="expression-input-node expression-input-node--variable expression-input-node--anchored"
      :data-id="node.id"
      :data-variable-name="node.code"
      v-bind="$attrs"
      @pointerenter="onMouseEnter"
      @pointermove="onMouseMove"
      @pointerleave="onMouseLeave"
      @pointerdown="onItemMouseDown"
    >
      <span class="expression-input-node__prefix">$</span>
      <span class="expression-input-node__variable">
        {{ t(`expressionVariables.${node.code}`) }}
      </span>

      <floating-element
        v-if="tooltip.open"
        :x="tooltip.x"
        :y="tooltip.y"
        class="expression-input-node__tooltip"
      >
        <header class="expression-input-node__tooltip-header">
          <span class="expression-input-node__tooltip-type">{{
            $t(`objectNames.variable`)
          }}</span>
        </header>
        <section class="expression-input-node__tooltip-body">
          <span class="pv-2 expression-input-node__tooltip-value">
            {{ formatNumber(variableExpressionValue?.value) }}
          </span>
        </section>
      </floating-element>
    </span>

    <span
      v-else-if="node.type == 'parenthesis'"
      ref="nodeElement"
      class="expression-input-node expression-input-node--parenthesis expression-input-node--anchored"
      :style="{
        '--level-color': LEVEL_COLORS[nestingLevel % LEVEL_COLORS.length]
      }"
      :class="{ '-multiline': isMultiline }"
      :data-id="node.id"
      v-bind="$attrs"
      @pointerdown="onMouseDown"
    >
      <span
        class="expression-input-node__parenthesis -opening"
        :class="{ '-provisory': node.opened == false }"
        :style="{ color: 'var(--level-color)' }"
        @pointerenter="onMouseEnter"
        @pointermove="onMouseMove"
        @pointerleave="onMouseLeave"
        @pointerdown="onBeginningMousedown"
        >&#40;</span
      >
      <expression-input-node
        :ref="(el) => (childNodeElements[0] = el as ChildNodeElement)"
        :node="node.args[0]"
        :level="0"
        :nesting-level="nestingLevel + 1"
        :editable="editable"
        @focus-start="emit('focus-start', $event)"
        @focus-end="emit('focus-end', $event)"
      />
      <span
        class="expression-input-node__parenthesis -closing"
        :class="{ '-provisory': node.closed == false }"
        :style="{ color: 'var(--level-color)' }"
        @pointerdown="onEndMousedown"
        >&#41;</span
      >

      <floating-element
        v-if="tooltip.open"
        :x="tooltip.x"
        :y="tooltip.y"
        class="expression-input-node__tooltip"
      >
        <header class="expression-input-node__tooltip-header">
          <span class="expression-input-node__tooltip-type">
            {{ $t("labels.parenthesis") }}
          </span>
        </header>
        <section class="expression-input-node__tooltip-body">
          <span class="pv-2 expression-input-node__tooltip-value">
            {{ formatNumber(evaluatedNode?.value) }}
          </span>
        </section>
      </floating-element>
    </span>

    <span
      ref="anchorAfter"
      :contenteditable="editable"
      class="expression-input-node__cursorholder -after"
      :data-connected-id="node.id"
      @pointerdown="onAnchorAfterMouseDown"
      @pointermove="onAnchorAfterMouseMove"
    ></span>
  </span>
</template>

<script setup lang="ts">
import { computed, toValue, ref, inject, reactive, onMounted, nextTick } from "vue";
import { watchDebounced } from "@vueuse/core";
import { useI18n } from "vue-i18n";
import { formatNumber } from "@/helpers/utility/Formatting";

import FloatingElement from "@core/ui/FloatingElement.vue";

import { functionsList } from "@editor/items/expression/expressionFunctions";
import { calculateExpression } from "@editor/items/expression/expressionFunctions";

import type { Ref } from "vue";
import type { ExpressionValue } from "@editor/items/expression/expressionFunctions";
import type { ChildNodeElement, InputNode } from "./ExpressionInputTypes";
import type { ExpressionParseContext } from "../effect/Expression";
import type { ExpressionTree } from "./ExpressionTree";

defineOptions({
  name: "ExpressionInputNode",
  inheritAttrs: false
});

const props = withDefaults(
  defineProps<{
    node: InputNode;
    level: number;
    nestingLevel?: number;
    placeholder?: string;
    editable?: boolean;
  }>(),
  {
    editable: true,
    nestingLevel: 0
  }
);

const emit = defineEmits<{
  (e: "focus-previous-node"): void;
  (e: "focus-next-node"): void;
  (e: "focus-start", event: { node: InputNode; offset: number }): void;
  (e: "focus-end", event: { node: InputNode; offset: number }): void;
}>();

const injectedParseContext = inject("parseContext");
const parseContext = computed(
  () =>
    (injectedParseContext as undefined | { value: ExpressionParseContext })
      ?.value
);
const injectedTree = inject<ExpressionTree>("expressionTree");

const nodeElement = ref<HTMLDivElement>();
const childNodeElements = ref<(ChildNodeElement | null)[]>([]);
const anchorBefore = ref<HTMLDivElement>();
const anchorAfter = ref<HTMLDivElement>();
const functionOpenElement = ref<HTMLDivElement>();
const functionCloseElement = ref<HTMLDivElement>();

const { t } = useI18n();

const tooltip = reactive({
  open: false,
  x: 0,
  y: 0
});

const name = computed(() => {
  if (props.node.type == "constant") return props.node.value;
  if (props.node.type == "operator") return props.node.op;
  if (props.node.type == "function")
    return t(`expressionFunctions.${props.node.name}`);
  return "";
});

const connectors = computed(() => {
  if (props.node.type != "function") return [];

  const functionData = functionsList[props.node.name];
  if (functionData.args < 2) return [];
  if (functionData.connectors)
    return functionData.connectors.map((c) => t(`expressionSeparators.${c}`));
  return [];
});

// ========================
// Mousedown functions

function onMouseDown(event: MouseEvent) {
  if (event.defaultPrevented) return;
}

function onOperatorMousedown(event: MouseEvent) {
  if (event.defaultPrevented) return;

  const fields = childNodeElements.value || [];

  // depending on where the operator was clicked, focus the left or right field
  focusClosest(event.target as HTMLElement, event.x, fields[0], fields[1]);

  event.preventDefault();
}

function onBeginningMousedown(event: MouseEvent) {
  if (event.defaultPrevented) return;

  const fields = childNodeElements.value || [];

  // depending on where the operator was clicked, focus the left or right field
  focusClosest(event.target as HTMLElement, event.x, null, fields[0]);

  event.preventDefault();
}

function onFunctionSeparatorMousedown(event: MouseEvent, index: number) {
  if (event.defaultPrevented) return;

  const fields = childNodeElements.value || [];

  // depending on where the operator was clicked, focus the left or right field
  focusClosest(
    event.target as HTMLElement,
    event.x,
    fields[index],
    fields[index + 1]
  );

  event.preventDefault();
}

function onEndMousedown(event: MouseEvent) {
  if (event.defaultPrevented) return;

  const fields = childNodeElements.value || [];

  // depending on where the operator was clicked, focus the left or right field
  focusClosest(
    event.target as HTMLElement,
    event.x,
    fields[fields.length - 1],
    null
  );

  event.preventDefault();
}

function onItemMouseDown(event: MouseEvent) {
  if (event.defaultPrevented) return;

  // depending on where the operator was clicked, focus the left or right field
  focusClosest(event.target as HTMLElement, event.x, null, null);

  event.preventDefault();
}

function onConstantMouseDown(event: MouseEvent) {
  if (event.defaultPrevented) return;

  if (!nodeElement.value) return;

  const startOffset = getCaretOffsetAtPosition(
    nodeElement.value.childNodes[0],
    event.x,
    event.y
  );

  focusContenteditable(nodeElement.value, startOffset);

  event.preventDefault();
}

function onAnchorBeforeMouseDown(event: MouseEvent) {
  if (event.defaultPrevented) return;
  emit("focus-start", { node: props.node, offset: 0 });
  event.preventDefault();
}
function onAnchorAfterMouseDown(event: MouseEvent) {
  if (event.defaultPrevented) return;
  emit("focus-start", { node: props.node, offset: 1 });
  event.preventDefault();
}

function onMouseEnter(mouseEvent: MouseEvent) {
  tooltip.open = true;
  tooltip.x = mouseEvent.x;
  tooltip.y = mouseEvent.y + 20;
  console.log("open tooltip!");
}
function onMouseMove(mouseEvent: MouseEvent) {
  tooltip.x = mouseEvent.x;
  tooltip.y = mouseEvent.y + 20;
}
function onMouseLeave() {
  tooltip.open = false;
}

/**
 * Returns the startOffset a caret range should have to be at the given position
 */
function getCaretOffsetAtPosition(textNode: Node, x: number, y: number) {
  if (textNode == null) return 0;

  const textLength = textNode.textContent?.length || 0;
  if (textLength == 0) return 0;

  const range = document.createRange();
  range.selectNodeContents(textNode);
  const rects = range.getClientRects();
  if (rects.length == 0) return 0;

  // reduce the size of the range until only the characters where the mouse is over are left
  let left = 0;
  let right = textLength;
  while (right - left > 1) {
    const middle = Math.floor((left + right) / 2);
    range.setStart(textNode, left);
    range.setEnd(textNode, middle);
    const middleRects = range.getClientRects();
    if (middleRects.length == 0) return 0;

    const relevantRect = Array.from(middleRects).find(
      (r) => r.bottom >= y && r.top <= y
    );
    if (relevantRect == null) {
      left = middle;
    } else {
      if (relevantRect.bottom < y) {
        left = middle;
      } else if (relevantRect.top > y) {
        right = middle;
      } else if (relevantRect.left > x) {
        right = middle;
      } else if (relevantRect.right < x) {
        left = middle;
      } else {
        right = middle;
      }
    }
    // await new Promise((resolve) => setTimeout(resolve, 100));
  }

  range.setStart(textNode, left);
  range.setEnd(textNode, right);

  const rangeRect = range.getBoundingClientRect();
  // drawRectBoxes([rangeRect]);

  const distanceLeft = Math.abs(x - rangeRect.left);
  const distanceRight = Math.abs(x - rangeRect.right);

  // return the side of the offset that's closer to the mouse
  if (distanceLeft < distanceRight) return left;
  else return right;
}

// ========================
// Mouseover functions

function onConstantMouseMove(event: MouseEvent) {
  if (event.defaultPrevented) return;

  // is main mouse button pressed?
  if (event.buttons != 1) return;

  if (!nodeElement.value) return;

  const offset = getCaretOffsetAtPosition(
    nodeElement.value.childNodes[0],
    event.x,
    event.y
  );

  emit("focus-end", { node: props.node, offset: offset });

  event.preventDefault();
}

function onAnchorBeforeMouseMove(event: MouseEvent) {
  if (event.defaultPrevented) return;

  // is main mouse button pressed?
  if (event.buttons != 1) return;

  emit("focus-end", { node: props.node, offset: 0 });

  event.preventDefault();
}

function onAnchorAfterMouseMove(event: MouseEvent) {
  if (event.defaultPrevented) return;

  // is main mouse button pressed?
  if (event.buttons != 1) return;

  emit("focus-end", { node: props.node, offset: 1 });

  event.preventDefault();
}

// ========================
// Focus functions

function focusClosest(
  element: HTMLElement,
  x: number,
  leftNode: ChildNodeElement | null,
  rightNode: ChildNodeElement | null
) {
  const rect = element.getBoundingClientRect();
  if (!rect) {
    console.log("no rect");
    return;
  }

  const leftDistance = Math.abs(rect.left - x);
  const rightDistance = Math.abs(rect.right - x);

  if (leftDistance < rightDistance) {
    if (leftNode) {
      console.log("left field!");
      leftNode.focusRight();
    } else {
      if (anchorBefore.value) {
        focusContenteditable(anchorBefore.value);
      } else {
        emit("focus-previous-node");
      }
    }
  } else {
    if (rightNode) {
      console.log("right field!");
      rightNode.focusLeft();
    } else {
      if (anchorAfter.value) {
        focusContenteditable(anchorAfter.value);
      } else {
        emit("focus-next-node");
      }
    }
  }
}

function focusContenteditable(
  element: HTMLElement,
  startOffset?: number,
  endOffset?: number
) {
  if (startOffset == null) {
    startOffset = 0;
    endOffset = element.textContent?.length || 0;
  } else if (endOffset == null) {
    endOffset = startOffset;
  }

  if (element == nodeElement.value) {
    emit("focus-start", { node: props.node, offset: startOffset });
    emit("focus-end", { node: props.node, offset: endOffset });
  }
  if (element == anchorBefore.value) {
    emit("focus-start", { node: props.node, offset: 0 });
    emit("focus-end", { node: props.node, offset: 0 });
  }
  if (element == anchorAfter.value) {
    emit("focus-start", { node: props.node, offset: 1 });
    emit("focus-end", { node: props.node, offset: 1 });
  }
}

function focus(startOffset?: number, endOffset?: number) {
  if (!nodeElement.value) return;

  if (props.node.type == "constant") {
    focusContenteditable(nodeElement.value, startOffset, endOffset);
    return;
  }

  if (startOffset == null || startOffset == 0) {
    if (anchorBefore.value) {
      focusContenteditable(anchorBefore.value);
      return;
    }
  } else {
    if (anchorAfter.value) {
      focusContenteditable(anchorAfter.value);
      return;
    }
  }

  if (childNodeElements.value.length > 0) {
    childNodeElements.value[0]?.focus(startOffset, endOffset);
    return;
  }
}

function focusRight() {
  if (!nodeElement.value) return;

  if (anchorAfter.value) {
    focusContenteditable(anchorAfter.value);
    return;
  }

  if (props.node.type == "constant") {
    focusContenteditable(
      nodeElement.value,
      nodeElement.value.textContent?.length
    );
  }

  if (props.node.type == "operator") {
    childNodeElements.value?.[1]?.focusRight();
  }
}

function focusLeft() {
  if (!nodeElement.value) return;

  if (anchorBefore.value) {
    focusContenteditable(anchorBefore.value);
    return;
  }

  if (props.node.type == "constant") {
    focusContenteditable(nodeElement.value, 0);
  }

  if (props.node.type == "operator") {
    childNodeElements.value?.[0]?.focusLeft();
  }
}

// ========================
// Size computed

const inputBottom = inject<{ bottom: Ref<number> }>("inputBounding");
const isMultiline = ref(false);
function updateMultiline() {
  if (props.node.type == "function") {
    if (!functionOpenElement.value || !functionCloseElement.value) return;
  
    const openTop = functionOpenElement.value.getBoundingClientRect().top;
    const closeTop = functionCloseElement.value.getBoundingClientRect().top;
  
    const difference = Math.abs(openTop - closeTop);
  
    if (difference > 2) {
      isMultiline.value = true;
    } else {
      isMultiline.value = false;
    }
  } else if (props.node.type == "parenthesis") {
    const parentheses = nodeElement.value?.querySelectorAll(
      ".expression-input-node__parenthesis"
    );

    if (!parentheses) return;

    const openTop = parentheses[0].getBoundingClientRect().top;
    const closeTop = parentheses[1].getBoundingClientRect().top;

    const difference = Math.abs(openTop - closeTop);

    if (difference > 2) {
      isMultiline.value = true;
    } else {
      isMultiline.value = false;
    }
  }
}
watchDebounced(
  () => inputBottom?.bottom?.value,
  () => {
    updateMultiline();
  },
  {
    debounce: 500,
    maxWait: 2000
  }
);
onMounted(() => {
  updateMultiline();
});
if (injectedTree) {
  injectedTree.on("tree-updated", () => {
    isMultiline.value = false;
    nextTick(() => {
      updateMultiline();
    });
  });
}

// ========================
// OPERATOR Computed

const operatorIsAtStart = computed(() => {
  if (props.node.type != "operator") return false;

  
  const firstArg = props.node.args[0];
  if (firstArg.type != "constant") return false;
  if (firstArg.value.trim() != "") return false;
  
  return props.node.id == injectedTree?.state.root?.id;
});

// ========================
// Size functions

function getBoundingClientRect() {
  return nodeElement.value?.getBoundingClientRect();
}

function getCenter() {
  const rect = getBoundingClientRect();
  if (!rect) return null;
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2
  };
}

function getChildNodes(): ChildNodeElement[] {
  const elements = [];
  for (const child of childNodeElements.value || []) {
    if (!child) continue;
    elements.push(child);
    const childInputNodes = child.getChildNodes();
    elements.push(...childInputNodes);
  }
  return elements;
}

defineExpose({
  type: computed(() => props.node.type),
  focus,
  focusLeft,
  focusRight,
  getBoundingClientRect,
  getChildNodes,
  getCenter
});

// ========================
// Utils

// colors, legible for all backgrounds
const LEVEL_COLORS = computed(() => [
  "#2E5BFF",
  "#1D9A0A",
  "#FF8C00",
  "#d47d13",
  "#B22222"
]);

// /**
//  * Debug: draw boxes around the given client rects
//  */
// function drawRectBoxes(clientRects: Iterable<DOMRect>) {
//   Array.from(document.querySelectorAll(".domrect-box")).forEach((node) => {
//     node.remove();
//   });

//   Array.from(clientRects).forEach((rect) => {
//     console.log("create domrect-box");
//     var box = document.createElement("div");
//     box.classList.add("domrect-box");
//     box.style.top = rect.y + "px";
//     box.style.left = rect.x + "px";
//     box.style.width = rect.width + "px";
//     box.style.height = rect.height + "px";
//     box.style.backgroundColor = "rgba(255, 0, 0, 0.5)";
//     box.style.position = "absolute";
//     box.style.pointerEvents = "none";
//     document.body.appendChild(box);
//   });
// }

const parseContextRound = computed(() => {
  if (!parseContext.value) return undefined;
  const rounds = parseContext.value.rounds;
  if (!rounds) return undefined;
  const round = rounds.find((r) => r.index == parseContext.value?.roundIndex);
  return round ?? rounds[rounds.length - 1];
});

const nodeItem = computed(() => {
  if (!("item" in props.node)) return undefined;
  return toValue(props.node.item);
});

const itemExpressionValue = computed((): ExpressionValue | null => {
  const item = nodeItem.value;
  if (!item) return null;

  const value = item.value;
  const round = parseContextRound.value;

  if (!round) {
    return {
      value: value ?? 0,
      minValue: item.minValue ?? null,
      maxValue: item.maxValue ?? null,
      unit: item.unit || "",
      unitValid: true,
      valid: true
    };
  }

  return (
    round.elementValues?.[item.uid] || {
      value: value ?? 0,
      minValue: item.minValue ?? null,
      maxValue: item.maxValue ?? null,
      unit: item.unit || "",
      unitValid: true,
      valid: true
    }
  );
});

// ========================
// VARIABLES

const variableExpressionValue = computed(() => {
  const type = props.node.type;
  if (type != "variable") return null;
  const variable = props.node.code;

  const context = parseContext.value;
  if (!context) return null;

  try {
    return calculateExpression(`${variable}`, context);
  } catch (e) {
    return null;
  }
});

// ========================
// NODE

const evaluatedNode = computed(() => {
  return injectedTree?.evaluateNode(props.node) ?? null;
});
</script>

<style lang="scss">
.expression-input-node {
  min-height: 1.5rem;
  min-width: 0.25rem;
  user-select: auto;
  font-family: var(--font-family-ui);

  &:focus {
    outline: none;
  }

  &--constant {
    padding: 0.125rem 0.25rem;
    white-space: pre-wrap;
    overflow-wrap: break-word;
    max-width: 100%;

    &.-empty {
      box-shadow: none;
    }

    &:empty::before {
      content: attr(data-placeholder);
      color: var(--color-text-light);
    }
  }

  &--operator {
    max-width: 100%;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    row-gap: 0.25rem;
    // outline: 1px dashed var(--group-color);
    // outline-offset: calc(var(--node-level) * -1px + 2px);
    &.-no-break {
      flex-wrap: nowrap;
    }
  }

  &__operator {
    background-color: var(--color-background-medium);
    border-radius: 5px;
    padding: 0.125rem 0.25rem;
    height: 1.25rem;
    min-width: 1.25rem;
    line-height: 1;
    font-size: 1rem;
    text-align: center;
    flex-shrink: 0;
    color: var(--color-text);

    &.-implicit {
      padding: 0.125rem 3px;
      // width: 0.5rem;
      min-width: 0;
      background-color: var(--color-background-shallow);

      &::before {
        content: "*";
        color: var(--color-text-light);
      }
    }
  }

  &--operator > &--constant.-empty:first-child {
    padding-left: 1px;
    padding-right: 1px;
  }

  &__cursorholder {
    padding: 0.125rem 0.25rem;
    &:focus {
      outline: none;
    }
    .-multiline > &.-before {
      align-self: flex-start;
    }
    .-multiline > &.-after {
      align-self: flex-end;
    }
  }

  &--function {
    max-width: 100%;
    min-width: 0;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    box-shadow: 0 1px 3px var(--color-shadow-2);
    border-radius: 0.25rem;
    row-gap: 0.25rem;
    padding: 4px 0;
    padding-left: 1rem;
    background-color: var(--color-background-close);
  }
  &--function.-multiline {
    flex-direction: column;
    align-items: flex-start;
    border-left: 1px solid var(--level-color);
  }

  // &--function &--constant {
  //   background-color: hsl(var(--color-primary-h), 20%, 96%);
  // }

  &__functionopen {
    height: 1.3rem;
    padding-right: 0.4rem;
    padding-left: 0.25rem;

    & > * {
      pointer-events: none;
    }
  }
  &--function > &__functionopen {
    margin-left: -0.8rem;
  }

  &__functionname {
    font-weight: bold;
    // color: var(--color-text-medium);
    font-size: 12px;
    padding-right: 0.125rem;
    white-space: nowrap;
    // font-style: italic;
  }

  &__functionbody {
    max-width: 100%;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    width: auto;
  }
  &--function.-multiline > &__functionbody {
    padding-block: 0.25rem;
    padding-right: 0.5rem;
    flex-direction: column;
    align-items: flex-start;
  }

  &__functionseparator {
    position: relative;
    color: var(--color-text-moderate);
    font-weight: 600;
    font-size: 12px;
    padding: 0.25rem 0.4rem;
    // font-style: italic;
  }
  &--function.-multiline > &__functionbody > &__functionseparator {
    padding-block: 0.5rem;
    left: -0.5rem;
  }

  &__functionclose {
    height: 1.3rem;
    position: relative;
    padding-left: 0.4rem;
    padding-right: 0.25rem;
  }
  &--function.-multiline > &__functionclose {
    left: -0.8rem;
  }

  &--item,
  &--variable {
    display: flex;
    align-items: center;
    box-shadow: 0 2px 3px var(--color-shadow-2);
    border-radius: 0.8rem;
    overflow: hidden;
    background-color: var(--color-grey-lighter);
    cursor: default;
    max-width: 100%;
    overflow: hidden;

    &.-invalid {
      color: var(--color-error) !important;
      box-shadow: none;
      outline: 1px dashed var(--color-error);
    }
  }

  &__item {
    padding: 0.25rem;
    padding-right: 0.5rem;
    overflow: hidden;
    min-width: 0;
    text-overflow: ellipsis;
  }

  &__variable {
    padding: 0.25rem;
    padding-right: 0.5rem;
  }

  &__prefix {
    color: var(--color-text-medium);
    font-weight: 600;
    font-size: 12px;
    padding: 0.25rem 0.125rem;
    background-color: var(--color-background-deep);
  }

  &--parenthesis {
    display: flex;
    align-items: center;
    box-shadow: 0 1px 4px var(--color-shadow-2);
    padding: 0.25rem 0;
    border-radius: 0.8rem;
  }

  &__parenthesis {
    padding-block: 0.25rem;
    color: var(--color-text-medium);
    font-weight: 600;
    align-self: center;

    &.-provisory {
      opacity: 0.4;
    }

    &:first-child {
      padding-left: 0.25rem;
      .-multiline > & {
        align-self: flex-start;
      }
    }
    &:last-child {
      padding-right: 0.25rem;
      .-multiline > & {
        align-self: flex-end;
      }
    }
  }

  &__line {
    max-width: 100%;
    min-width: 0;
    display: flex;
    align-items: center;
  }

  &__tooltip {
    background-color: var(--color-background-deep);
    pointer-events: none;
    border-radius: 5px;
    box-shadow: 0 2px 4px var(--color-shadow-2);
  }
  &__tooltip-header {
    background-color: var(--color-background);
    padding: 0.5rem;
  }
  &__tooltip-body {
    padding: 0.5rem;
    padding-top: 0.25rem;
  }
  &__tooltip-type {
    font-size: 0.8rem;
  }
  &__tooltip-title {
    margin-bottom: 0;
    margin-top: 0.25rem;
    font-weight: 400;
  }
  &__tooltip-value {
    font-size: 1.25rem;
    font-weight: 600;
  }
  &__tooltip-unit {
    font-size: 0.8rem;
    font-weight: 400;
  }
}
</style>